<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  commands: {
    type: Array,
    default: () => [],
  },
  selectedIndex: {
    type: Number,
    default: 0,
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['select', 'hover', 'close']);

const menuRef = ref(null);

// Calculate position style to keep menu within viewport
const positionStyle = computed(() => {
  if (!props.position) return {};

  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const VIEWPORT_PADDING = 16;
  const MENU_WIDTH = 256; // w-64 is 256px
  const ESTIMATED_HEIGHT = 280;

  let left = props.position.x;

  // Horizontal adjustment
  if (left + MENU_WIDTH > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - MENU_WIDTH - VIEWPORT_PADDING;
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  }

  // Vertical adjustment
  const spaceBelow = viewportHeight - props.position.y - VIEWPORT_PADDING;
  const showAbove = spaceBelow < ESTIMATED_HEIGHT && props.position.y > spaceBelow;

  const style = {
    left: `${left}px`,
  };

  if (showAbove) {
    style.bottom = `${viewportHeight - props.position.y + 4}px`;
  } else {
    style.top = `${props.position.y + 24}px`;
  }

  return style;
});

const handleSelect = (command) => {
  emit('select', command);
};

const handleHover = (index) => {
  emit('hover', index);
};
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-40" @click="emit('close')"></div>

    <Transition
      enter-active-class="command-menu-enter"
      enter-from-class="command-menu-enter-from"
      enter-to-class="command-menu-enter-to"
      leave-active-class="command-menu-leave"
      leave-from-class="command-menu-leave-from"
      leave-to-class="command-menu-leave-to"
    >
      <div
        v-if="show"
        ref="menuRef"
        class="command-menu fixed z-50 w-64 bg-white/90 border border-neutral-200/80 rounded-xl shadow-xl shadow-black/10 overflow-hidden backdrop-blur-xl"
        :style="positionStyle"
      >
        <div class="px-3 pt-2.5 pb-1.5 border-b border-neutral-100/50">
          <span class="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider pl-1">
            {{ 'Commands' }}
          </span>
        </div>

        <div class="p-1.5 max-h-[280px] overflow-y-auto minimal-scrollbar">
          <div
            v-for="(cmd, index) in commands"
            :key="cmd.id"
            class="command-item flex items-center gap-3 px-2.5 py-2 cursor-pointer rounded-lg transition-all duration-200"
            :class="{
              'bg-neutral-100 text-neutral-900': index === selectedIndex,
              'text-neutral-600 hover:bg-neutral-50': index !== selectedIndex,
            }"
            @click="handleSelect(cmd)"
            @mousedown.prevent
            @mouseenter="handleHover(index)"
          >
            <!-- Icon placeholder or use SVG -->
            <div
              class="w-8 h-8 rounded-md flex items-center justify-center bg-white border border-neutral-100 shadow-sm text-neutral-500"
              :class="{ 'text-neutral-900 border-neutral-200': index === selectedIndex }"
            >
              <component :is="cmd.icon" v-if="cmd.icon" class="w-4 h-4" />
              <span v-else class="text-xs font-mono">{{ cmd.shortcut || '/' }}</span>
            </div>

            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-[13px] font-medium truncate">{{ cmd.label }}</span>
              <span class="text-[11px] text-neutral-400 truncate">{{ cmd.description }}</span>
            </div>

            <kbd
              v-if="index === selectedIndex"
              class="text-[10px] px-1.5 py-0.5 bg-white shadow-sm border border-neutral-100 text-neutral-400 rounded-md font-sans"
            >
              ↵
            </kbd>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.command-menu {
  /* Ensure it sits above other popovers */
  z-index: 100;
}

.command-menu-enter {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.command-menu-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.command-menu-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.command-menu-leave {
  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.command-menu-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.command-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.minimal-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.minimal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.minimal-scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
}

.minimal-scrollbar:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
}
</style>
